/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from '@auth0/auth0-react';

// react-router components
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import theme from "assets/theme";
import { useSoftUIController, setMiniSidenav, setOpenConfigurator } from "context";

// components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { Box, Grid } from "@mui/material";
import SoftBox from "components/SoftBox";

// mapbox and routes
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import routes from "./routes-new";
import { useQuery } from '@tanstack/react-query';

// API/MODELS
import deviceAPI from "api/bks";
import DashboardCardLayout from "nextus/Dashboards/DashboardCardLayout";
import DashboardNavbar from "nextus/Dashboards/DashboardNavbar";
import SideNav from "nextus/Sidenavs/SideNav";
import DashboardCardNavbar from "nextus/Dashboards/DashboardCardNavbar";
import pxToRem from "assets/theme/functions/pxToRem";
import breakpoints from "assets/theme/base/breakpoints";
import './app.css'; // Import the CSS file

export default function App() {
  const { loginWithRedirect, isAuthenticated, isLoading, getAccessTokenWithPopup } = useAuth0();

  const mapContainer = useRef(null);
  const map = useRef(null);
  mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, hiddenSidenav, direction, layout, openConfigurator, sidenavColor, deviceStyle } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);

  const [isLoaded, setIsLoaded] = useState(0);
  const [mapEnabled, setMapEnabled] = useState(true);
  const [navTitle, setNavTitle] = useState(['Landing', 'landing']);
  const [sidebarState, setSidebarState] = useState("landing");

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    if (!map.current && mapContainer.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
        center: [0, 0],
        zoom: 1,
        maxZoom: 20,
      });

      map.current.on('load', () => {
        setIsLoaded(1);
      });
    }
  });

  useEffect(() => {
    var padding = {};
    // console.log('APP STATE', sidebarState)

    if (sidebarState == 'sidenav') {
      padding = {
        left: 0,
        bottom: 0
      };
      if (map.current) {
        if (sidebarState == 'sidenav') {
          map.current.easeTo({
            padding: padding,
            duration: 100
          });
        }
        map.current.interactive = false
      }
      setMapEnabled(false);
    }
    else if (sidebarState == 'hidden') {
      setMapEnabled(false);
    }
    else {
      if (deviceStyle == 'mobile') {
        padding = {
          left: 0,
          bottom: 0
        };

        if (map.current) {
          map.current.interactive = sidebarState == 'exploring'
        }

        setMapEnabled(sidebarState == 'exploring');
      }
      else {
        padding = {
          left: 600,
          bottom: 290
        };

        if (map.current) {
          map.current.interactive = sidebarState == 'exploring'
        }

        setMapEnabled(true);
      }
    }

  }, [sidebarState, isLoaded]);

  const renderRoutes = useMemo(() => routes.map(function (route, key) {
    return (
      <Route exact path={route.route} key={key} element={<route.component map={map} navTitle={navTitle} setNavTitle={setNavTitle} sidebarState={sidebarState} setSidebarState={setSidebarState} />} />
    );
  }), [routes]);


  if (isLoading) {
    return "Loading";
  }

  if (!isAuthenticated) {
    loginWithRedirect();
    return;
  }

  var content_margin = 0;
  if (!hiddenSidenav && miniSidenav) {
    content_margin = pxToRem(132);
  }
  else if (!hiddenSidenav && !miniSidenav) {
    content_margin = pxToRem(374);
  }

  if (sidebarState == 'hidden') {
    return (
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <Routes>
            {isLoaded && renderRoutes}
          </Routes>
        </ThemeProvider>
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <Box component="div" ref={mapContainer} sx={{ height: '100%', width: '100%', position: 'absolute', top: '0px', left: '0px', 'z-index': 0, overflow: 'hidden' }} />
        <Box component="div" sx={{ maxHeight: '100%', maxWidth: '100%', 'z-index': 1000, pointerEvents: mapEnabled ? 'none' : 'auto', overflow: 'hidden' }} >
          <DashboardCardLayout sidebarState={sidebarState}>
            {(sidebarState == 'landing' || sidebarState == 'exploring' || sidebarState == 'hidden') && (
              <Routes>
                {isLoaded && renderRoutes}
              </Routes>
            )}
            {sidebarState == 'sidenav' && (
              <>
                <SideNav sidebarState={sidebarState} onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave} />
                <SoftBox height="100%" sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
                  marginLeft: content_margin,
                  transition: transitions.create("all", {
                    easing: transitions.easing.sharp,
                    duration: transitions.duration.shorter,
                  }),
                })}>
                  <Grid container spacing={3} sx={{ height: window.innerWidth < breakpoints.values.xs ? 'calc(100% + 8px)' : 'calc(100% + 24px)' }} alignItems={'flex-start'}>
                    <Grid item xxs={12}>
                      <DashboardCardNavbar navTitle={navTitle} />
                    </Grid>
                    <Grid item xxs={12} sx={{ height: 'calc(100% - 104px)' }}>
                      <Routes>
                        {isLoaded && renderRoutes}
                      </Routes>
                    </Grid>
                  </Grid>
                </SoftBox>
              </>
            )}
          </DashboardCardLayout>
        </Box>
      </ThemeProvider>
    </BrowserRouter>
  )
}
